import React, { FC, useEffect } from 'react';
import { Outlet, type RouteObject } from 'react-router-dom';
import { CspPath } from '../../../CspPath';
import { PathUtil } from '../util/PathUtil';
import { useNavigationContext } from '../context/useNavigationContextState';
import { useMatchPaths } from '../hooks/useMatchPaths';
import { PermissionCheckComponent } from '../../../common/component/layout/PermissionCheckComponent';
import { PermissionType } from '../../security/type/PermissionType';

const toRelativePath = (absolutePath: CspPath): string =>
  PathUtil.toRelativePath(CspPath.PATIENT_LIST, absolutePath, true);

const PatientListRoot: FC = () => {
  const { setSelectedSubPath } = useNavigationContext();
  const matchedCspPath = useMatchPaths([
    CspPath.PATIENT_LIST_STANDARD,
    CspPath.PATIENT_LIST_CLOSEOUT,
    CspPath.PATIENT_LIST_VISIT,
    CspPath.PATIENT_LIST_HBS,
    CspPath.PATIENT_LIST_HBS_DETAILS,
    CspPath.PATIENT_LIST_RPM_EVENTS,
    CspPath.PATIENT_LIST_DEBUG,
  ]);

  useEffect(() => {
    if (matchedCspPath) {
      setSelectedSubPath(CspPath.PATIENT_LIST, matchedCspPath);
    }
  }, [matchedCspPath, setSelectedSubPath]);

  return <Outlet />;
};

const PatientListStandardContainer = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */ '../../patient-list/patient-list-standard/container/PatientListStandardContainer'
    ),
);
const PatientListCloseoutContainer = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */ '../../patient-list/patient-list-closeout/container/PatientListCloseoutContainer'
    ),
);
const PatientListVisitContainer = React.lazy(
  () => import(/* webpackPrefetch: true */ '../../patient-list/patient-list-visit/container/PatientListVisitContainer'),
);
const PatientListBioSampleContainer = React.lazy(
  () => import(/* webpackPrefetch: true */ '../../hbs/hbs-list/container/PatientListBioSampleContainer'),
);
const PatientListRpmEventsContainer = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */ '../../patient-list/patient-list-rpm-events/container/PatientListRpmEventsContainer'
    ),
);
const PatientListDebugContainer = React.lazy(
  () => import(/* webpackPrefetch: true */ '../../patient-list/patient-list-debug/container/PatientListDebugContainer'),
);
const PatientListDefaultRoute = React.lazy(
  () => import(/* webpackPrefetch: true */ '../../patient-list/patient-list-common/component/PatientListDefaultRoute'),
);

export const PatientListRoutes: RouteObject[] = [
  {
    element: (
      <PermissionCheckComponent permission={PermissionType.CAN_VIEW_PATIENTS}>
        <PatientListRoot />
      </PermissionCheckComponent>
    ),
    children: [
      {
        path: toRelativePath(CspPath.PATIENT_LIST_STANDARD),
        element: <PatientListStandardContainer />,
      },
      {
        path: toRelativePath(CspPath.PATIENT_LIST_CLOSEOUT),
        element: <PatientListCloseoutContainer />,
      },
      {
        path: toRelativePath(CspPath.PATIENT_LIST_VISIT),
        element: <PatientListVisitContainer />,
      },
      {
        path: toRelativePath(CspPath.PATIENT_LIST_HBS),
        element: <PatientListBioSampleContainer />,
      },
      {
        path: toRelativePath(CspPath.PATIENT_LIST_HBS_DETAILS),
        element: <PatientListBioSampleContainer />,
      },
      {
        path: toRelativePath(CspPath.PATIENT_LIST_RPM_EVENTS),
        element: <PatientListRpmEventsContainer />,
      },
      {
        path: toRelativePath(CspPath.PATIENT_LIST_DEBUG),
        element: <PatientListDebugContainer />,
      },
      {
        path: '*',
        element: <PatientListDefaultRoute />,
      },
    ],
  },
];
